/** Lignes et zones de la ZO (docs/15-fuseaux-doctrinaux.md §3, §4.1, §4.3) : portage client de
 *  `generex_engine.zo_measures`, pour l'aperçu pendant un glissement ou un ⌘Z (le serveur fait foi au relâcher).
 *
 *  - Repère : (s, t) du modèle du fuseau (registre K2) ; une ligne a `ends = [sG, sD]` (limite gauche t = −1,
 *    droite t = +1) et se trace en 17 points de la gauche à la droite (`lineCoords`), extrapolée au-delà de la
 *    limite avant (`extrapolate`, FSCL).
 *  - Positions : `derive` refait le calcul du serveur à partir de ses entrées (`MeasuresOut.inputs` : FLOT
 *    calculée par temps, unité de manœuvre la plus en arrière, proportions, fractions de l'axe) et des positions
 *    choisies — mêmes bornes, même ordre, mêmes absences. Seuls manquent ce qui demande les unités elles-mêmes :
 *    l'unité qui porte la FLOT calculée (`basis`) et les avertissements sur les unités.
 *  - Écritures : `applyChange` (structure), `orderError` (règles bloquantes limitées à la ligne modifiée, §3.5),
 *    `slideRange` / `endRange` (butées d'un glissement ou d'une inclinaison, voisine qui arrête, temps compris, départ
 *    hors des butées, ligne figée), `clampSlide`, `admits`.
 *  - Noms : `NAMES` (copie du catalogue du serveur, pour une ligne rétablie pendant un aperçu).
 *  - Puces : `stackTags` (anti-recouvrement des boîtes à l'écran, puces des LIMA comme des lignes de la ZO : glissement
 *    vers l'extérieur, au-delà du bout de la ligne ; obstacles : marques d'échelon, puces des LIMA, traits voisins),
 *    `zoneNamePoint` / `zoneNameSpot` (nom d'une zone à l'écart de l'axe, à une place libre des symboles et des traits).
 *
 *  Vérifié contre le jeu de vecteurs du serveur `__fixtures__/measures.json` (tracés à 0,5 m, fractions à 1e-9). */

import type {
  MeasureAbsence,
  MeasureBasis,
  MeasureInputs,
  MeasureKey,
  MeasureKind,
  MeasureLine,
  MeasureNames,
  Measures,
  MeasureStyle,
  MeasureZone,
  MeasureZoneKey,
  Phase,
  PlacementMeasures,
} from "../../api/types";
import { PLACED_LEVELS, type PlacedLevel } from "../../echelons";
import { bearingDeg, type LonLat, moveM } from "../geo";
import type { CorridorFrame } from "./corridorFrame";

// --- Catalogue (zo_measures.LEVEL_LINES, LEVEL_ZONES, KINDS, STYLES) -------------------------------------------

export type MeasureLevel = PlacedLevel;

export const MEASURE_LEVELS: readonly MeasureLevel[] = PLACED_LEVELS;
export const MEASURE_PHASES: readonly Phase[] = ["T1", "T2", "T3"];
export const LINE_KEYS: readonly MeasureKey[] = ["LIMAR", "ZOAR_ZOR", "ZOR_ZOP", "LIMAV", "FLOT", "CFL", "FSCL"];
/** Extrémités du fuseau : se déplacent avec lui, jamais stockées. */
export const BOUNDARIES: readonly MeasureKey[] = ["LIMAR", "LIMAV"];
/** Limites internes, de l'arrière vers l'avant. */
export const INTERNAL: readonly MeasureKey[] = ["ZOAR_ZOR", "ZOR_ZOP"];
/** Une position par temps. */
export const PHASED: readonly MeasureKey[] = ["FLOT", "CFL"];
export const REMOVABLE: readonly MeasureKey[] = ["CFL", "FSCL"];

export const LEVEL_LINES: Readonly<Record<MeasureLevel, readonly MeasureKey[]>> = {
  REGIMENT: ["LIMAR", "LIMAV", "FLOT", "CFL"],
  BRIGADE: ["LIMAR", "ZOR_ZOP", "LIMAV", "FLOT", "CFL"],
  DIVISION: ["LIMAR", "ZOAR_ZOR", "ZOR_ZOP", "LIMAV", "FLOT", "CFL", "FSCL"],
  // Compagnie (docs/21 §8) : ZO = LIMAR–LIMAV, comme au régiment ; pas de FSCL.
  COMPAGNIE: ["LIMAR", "LIMAV", "FLOT", "CFL"],
  // Corps d'armée (docs/08 §23) : comme la division ; FSCL proposée par le LCC.
  CORPS: ["LIMAR", "ZOAR_ZOR", "ZOR_ZOP", "LIMAV", "FLOT", "CFL", "FSCL"],
};

/** Zones de chaque niveau et lignes qui les bornent (arrière, avant). */
export const LEVEL_ZONES: Readonly<Record<MeasureLevel, Partial<Record<MeasureZoneKey, [MeasureKey, MeasureKey]>>>> = {
  REGIMENT: { ZO: ["LIMAR", "LIMAV"] },
  BRIGADE: { ZOR: ["LIMAR", "ZOR_ZOP"], ZOP: ["ZOR_ZOP", "LIMAV"] },
  DIVISION: { ZOAR: ["LIMAR", "ZOAR_ZOR"], ZOR: ["ZOAR_ZOR", "ZOR_ZOP"], ZOP: ["ZOR_ZOP", "LIMAV"] },
  COMPAGNIE: { ZO: ["LIMAR", "LIMAV"] },
  CORPS: { ZOAR: ["LIMAR", "ZOAR_ZOR"], ZOR: ["ZOAR_ZOR", "ZOR_ZOP"], ZOP: ["ZOR_ZOP", "LIMAV"] },
};

export const KINDS: Readonly<Record<MeasureKey, MeasureKind>> = {
  LIMAR: "ao_boundary",
  LIMAV: "ao_boundary",
  ZOAR_ZOR: "zone_limit",
  ZOR_ZOP: "zone_limit",
  FLOT: "flot",
  CFL: "cfl",
  FSCL: "fscl",
};

/** Motif et épaisseur de chaque ligne (table `STYLES` du serveur ; la carte applique ses propres tirets, §4.1). */
export const STYLES: Readonly<Record<MeasureKey, { style: MeasureStyle; weight: number }>> = {
  LIMAR: { style: "solid", weight: 3 },
  LIMAV: { style: "solid", weight: 3 },
  ZOAR_ZOR: { style: "solid", weight: 2 },
  ZOR_ZOP: { style: "solid", weight: 2 },
  FLOT: { style: "dashed", weight: 2 },
  CFL: { style: "dotted", weight: 2 },
  FSCL: { style: "dashdot", weight: 2 },
};

type NamePair = readonly [short: string, full: string];
const named = (fr: NamePair, en: NamePair): MeasureNames => ({
  fr: { short: fr[0], full: fr[1] },
  en: { short: en[0], full: en[1] },
});
const FLOT_NAMES = named(["FLOT", "Ligne avant des forces amies"], ["FLOT", "Forward line of own troops"]);

/** Noms des lignes et des zones par niveau (`zo_measures.NAMES`, docs/15 §3.1 ; puce : nom court, fiche : nom
 *  complet), copie du catalogue du serveur vérifiée sur le jeu de vecteurs : le serveur fait foi, la carte s'en sert
 *  pour une ligne qu'il n'a pas encore renvoyée (CFL ou FSCL rétablie pendant un aperçu). */
export const NAMES: Readonly<Record<MeasureLevel, Readonly<Partial<Record<MeasureKey | MeasureZoneKey, MeasureNames>>>>> = {
  REGIMENT: {
    LIMAR: named(
      ["LIMAR ZO GTIA", "Limite arrière de la zone d'opération du GTIA"],
      ["BG AO rear boundary", "Rear boundary of the battlegroup area of operations"],
    ),
    LIMAV: named(
      ["LIMAV ZO GTIA", "Limite avant de la zone d'opération du GTIA"],
      ["BG AO forward boundary", "Forward boundary of the battlegroup area of operations"],
    ),
    FLOT: FLOT_NAMES,
    CFL: named(
      ["CFL (GTIA)", "Ligne des feux coordonnés fixée par le GTIA"],
      ["CFL (BG)", "Coordinated fire line set by the battlegroup"],
    ),
    ZO: named(["ZO GTIA", "Zone d'opération du GTIA"], ["BG AO", "Battlegroup area of operations"]),
  },
  BRIGADE: {
    LIMAR: named(
      ["LIMAR ZO BIA", "Limite arrière de la zone d'opération de la brigade"],
      ["BDE AO rear boundary", "Rear boundary of the brigade area of operations"],
    ),
    ZOR_ZOP: named(
      ["LIMAV ZO GTIA de tête", "LIMAV des ZO GTIA de tête : limite ZOR BIA / ZOP BIA"],
      ["Lead BG AOs boundary", "Forward boundary of the lead battlegroups' AOs: brigade close area / deep area limit"],
    ),
    LIMAV: named(
      ["LIMAV ZO BIA", "Limite avant de la zone d'opération de la brigade"],
      ["BDE AO forward boundary", "Forward boundary of the brigade area of operations"],
    ),
    FLOT: FLOT_NAMES,
    CFL: named(
      ["CFL (BIA)", "Ligne des feux coordonnés fixée par la brigade"],
      ["CFL (BDE)", "Coordinated fire line set by the brigade"],
    ),
    ZOR: named(
      ["ZOR BIA", "Zone d'opération rapprochée de la brigade : zones d'opération des GTIA"],
      ["BDE close area", "Brigade close area: the battlegroups' AOs"],
    ),
    ZOP: named(["ZOP BIA", "Zone d'opération en profondeur de la brigade"], ["BDE deep area", "Brigade deep area"]),
  },
  DIVISION: {
    LIMAR: named(
      ["LIMAR ZODIV", "Limite arrière de la zone d'opération divisionnaire"],
      ["DIV AO rear boundary", "Rear boundary of the division area of operations"],
    ),
    ZOAR_ZOR: named(
      ["Limite ZOAR / ZOR DIV", "Limite ZOAR DIV / ZOR DIV : limite avant de la zone arrière de la division"],
      ["DIV rear area limit", "Division rear area / close area limit: forward limit of the division rear area"],
    ),
    ZOR_ZOP: named(
      ["LIMAV ZO BIA de tête", "LIMAV des ZO BIA de tête : limite ZOR DIV / ZOP DIV"],
      ["Lead BDE AOs boundary", "Forward boundary of the lead brigades' AOs: division close area / deep area limit"],
    ),
    LIMAV: named(
      ["LIMAV ZODIV", "Limite avant de la zone d'opération divisionnaire"],
      ["DIV AO forward boundary", "Forward boundary of the division area of operations"],
    ),
    FLOT: FLOT_NAMES,
    CFL: named(
      ["CFL (DIV)", "Ligne des feux coordonnés fixée par la division (feux sol-sol)"],
      ["CFL (DIV)", "Coordinated fire line set by the division (surface-to-surface fires)"],
    ),
    FSCL: named(
      ["FSCL", "Ligne de coordination des appuis feux : intercomposantes, une seule pour la composante terrestre"],
      ["FSCL", "Fire support coordination line: joint, one for the land component"],
    ),
    ZOAR: named(
      ["ZOAR DIV", "Zone arrière de la division : commandement et soutien"],
      ["DIV rear area", "Division rear area: command and sustainment"],
    ),
    ZOR: named(
      ["ZOR DIV", "Zone d'opération rapprochée de la division : zones d'opération des brigades"],
      ["DIV close area", "Division close area: the brigades' AOs"],
    ),
    ZOP: named(["ZOP DIV", "Zone d'opération en profondeur de la division"], ["DIV deep area", "Division deep area"]),
  },
  // Compagnie (docs/21 §8) : la CFL reste celle du GTIA.
  COMPAGNIE: {
    LIMAR: named(
      ["LIMAR ZO S/GTIA", "Limite arrière de la zone d'opération du S/GTIA"],
      ["Coy Tm AO rear boundary", "Rear boundary of the company team area of operations"],
    ),
    LIMAV: named(
      ["LIMAV ZO S/GTIA", "Limite avant de la zone d'opération du S/GTIA"],
      ["Coy Tm AO forward boundary", "Forward boundary of the company team area of operations"],
    ),
    FLOT: FLOT_NAMES,
    CFL: named(
      ["CFL (GTIA)", "Ligne des feux coordonnés fixée par le GTIA"],
      ["CFL (BG)", "Coordinated fire line set by the battlegroup"],
    ),
    ZO: named(["ZO S/GTIA", "Zone d'opération du S/GTIA"], ["Coy Tm AO", "Company team area of operations"]),
  },
  // Corps d'armée (docs/08 §23) : ZOR du corps = zones d'opération des divisions.
  CORPS: {
    LIMAR: named(
      ["LIMAR ZO CA", "Limite arrière de la zone d'opération du corps d'armée"],
      ["Corps AO rear boundary", "Rear boundary of the corps area of operations"],
    ),
    ZOAR_ZOR: named(
      ["Limite ZOAR / ZOR CA", "Limite ZOAR CA / ZOR CA : limite avant de la zone arrière du corps d'armée"],
      ["Corps rear area limit", "Corps rear area / close area limit: forward limit of the corps rear area"],
    ),
    ZOR_ZOP: named(
      ["LIMAV ZO DIV de tête", "LIMAV des ZO DIV de tête : limite ZOR CA / ZOP CA"],
      ["Lead DIV AOs boundary", "Forward boundary of the lead divisions' AOs: corps close area / deep area limit"],
    ),
    LIMAV: named(
      ["LIMAV ZO CA", "Limite avant de la zone d'opération du corps d'armée"],
      ["Corps AO forward boundary", "Forward boundary of the corps area of operations"],
    ),
    FLOT: FLOT_NAMES,
    CFL: named(
      ["CFL (CA)", "Ligne des feux coordonnés fixée par le corps d'armée (feux sol-sol)"],
      ["CFL (Corps)", "Coordinated fire line set by the corps (surface-to-surface fires)"],
    ),
    FSCL: named(
      ["FSCL", "Ligne de coordination des appuis feux : proposée par le LCC, une seule pour la composante terrestre"],
      ["FSCL", "Fire support coordination line: proposed by the LCC, one for the land component"],
    ),
    ZOAR: named(
      ["ZOAR CA", "Zone arrière du corps d'armée : commandement et soutien"],
      ["Corps rear area", "Corps rear area: command and sustainment"],
    ),
    ZOR: named(
      ["ZOR CA", "Zone d'opération rapprochée du corps d'armée : zones d'opération des divisions"],
      ["Corps close area", "Corps close area: the divisions' AOs"],
    ),
    ZOP: named(["ZOP CA", "Zone d'opération en profondeur du corps d'armée"], ["Corps deep area", "Corps deep area"]),
  },
};

export function isMeasureLevel(level: unknown): level is MeasureLevel {
  return typeof level === "string" && (MEASURE_LEVELS as readonly string[]).includes(level);
}

/** Lignes dont la position peut être choisie à ce niveau (LIMAR et LIMAV exclues). */
export function storable(level: MeasureLevel): MeasureKey[] {
  return LEVEL_LINES[level].filter((k) => !BOUNDARIES.includes(k));
}

// --- Constantes des positions calculées (§3.2) ------------------------------------------------------------------

export const LINE_ORDER_GAP_M = 50;
export const CFL_ADVANCE = 0.08;
export const CFL_ADVANCE_MIN_M = 1_000;
export const ZONE_MIN = 0.05;
export const ZONE_MIN_M = 1_000;
export const REAR_MARGIN = 0.03;
export const REAR_MARGIN_M = 500;
/** Unités retenues : |t| ≤ 1,25 (le serveur seul les voit). */
export const ELIGIBLE_T = 1.25;
export const FSCL_S = 1.1;
/** Corps d'armée (docs/08 §23) : FSCL calculée dans la ZOP du corps (cas général du mémento), et non au-delà de sa
 *  LIMAV. */
export const FSCL_BY_LEVEL: Readonly<Partial<Record<MeasureLevel, number>>> = { CORPS: 0.92 };
export const FSCL_MAX = 1.25;
export const MEMENTO_DEPTH_KM = 450;
/** Proportions du mémento : ZOAR / ZOR à 200/450 (division) ; ZOR / ZOP à 350/450 (division), 1/2 (brigade). Un
 *  niveau a une ZOR / ZOP s'il a une p₂, une ZOAR / ZOR (et la profondeur minimale 2z de la ZOR / ZOP) s'il a une p₁. */
/** Corps d'armée (docs/08 §23) : ZOAR 200 km, ZOR 450 km, ZOP 150 km sur 800 km (mémento p. 24). */
export const P1: Readonly<Partial<Record<MeasureLevel, number>>> = { DIVISION: 200 / MEMENTO_DEPTH_KM, CORPS: 200 / 800 };
export const P2: Readonly<Partial<Record<MeasureLevel, number>>> = {
  DIVISION: 350 / MEMENTO_DEPTH_KM,
  BRIGADE: 0.5,
  CORPS: 650 / 800,
};
export const SAMPLES = 16;
export const EXTRAPOLATION_FROM = 0.9;
const EPS = 1e-9;
/** Tolérance de la règle 1 du serveur (`_internal_chain_error`). */
const CHAIN_EPS = 1e-12;

/** Fractions de l'axe dépendant de sa longueur (`zo_measures._fractions`) : écart minimal entre lignes, avance de la
 *  CFL, profondeur minimale d'une zone, marge arrière. */
export function fractionsOf(length: number): Pick<MeasureInputs, "gap" | "cfl_gap" | "zone_min" | "rear_margin"> {
  const atLeast = (ratio: number, meters: number) => (length > 0 ? Math.max(ratio, meters / length) : ratio);
  return {
    gap: length > 0 ? LINE_ORDER_GAP_M / length : 0,
    cfl_gap: atLeast(CFL_ADVANCE, CFL_ADVANCE_MIN_M),
    zone_min: atLeast(ZONE_MIN, ZONE_MIN_M),
    rear_margin: atLeast(REAR_MARGIN, REAR_MARGIN_M),
  };
}

// --- Messages (F21, traduits à l'affichage par `tServer`) -------------------------------------------------------

export type MeasureRefusal =
  | "unknown"
  | "boundary"
  | "phase_required"
  | "phase_forbidden"
  | "range"
  | "off"
  | "format"
  | "order";

const PREFIX = "Ligne de la ZO refusée : ";
/** Texte exact des refus du serveur (`zo_measures.RULES`). */
export const RULES: Readonly<Record<MeasureRefusal, string>> = {
  unknown: PREFIX + "ligne inconnue pour ce niveau",
  boundary: PREFIX + "les limites arrière et avant se déplacent avec le fuseau",
  phase_required: PREFIX + "temps requis pour la FLOT et la CFL",
  phase_forbidden: PREFIX + "temps sans objet pour cette ligne",
  range: PREFIX + "position hors du fuseau",
  off: PREFIX + "seules la CFL et la FSCL peuvent être retirées",
  format: PREFIX + "format illisible",
  order: PREFIX + "ordre des lignes de la ZO non respecté",
};

export type MeasureWarning = "flot_beyond_cfl" | "units_beyond_flot" | "units_beyond_limit" | "fscl_short_of_flot";
/** Avertissements du serveur (`zo_measures.WARNINGS`), dans leur ordre de tri. */
export const WARNINGS: Readonly<Record<MeasureWarning, string>> = {
  flot_beyond_cfl: "La FLOT dépasse la CFL en {temps}",
  units_beyond_flot: "Des unités amies sont au-delà de la FLOT en {temps}",
  units_beyond_limit: "Des unités amies sont au-delà de la limite avant en {temps}",
  fscl_short_of_flot: "La FSCL est en deçà de la FLOT en {temps}",
};
const WARNING_KINDS = Object.keys(WARNINGS) as MeasureWarning[];

export function warningText(kind: MeasureWarning, phase: Phase): string {
  return WARNINGS[kind].replace("{temps}", phase);
}

// --- Positions choisies (`placement.measures`, §3.4) ------------------------------------------------------------

export type Ends = readonly [number, number];

/** Positions choisies normalisées : `fixed` (limites internes, FSCL), `phased` (FLOT et CFL par temps), `off`
 *  (CFL et FSCL retirées). */
export interface Overrides {
  fixed: Partial<Record<MeasureKey, Ends>>;
  phased: Partial<Record<MeasureKey, Partial<Record<Phase, Ends>>>>;
  off: MeasureKey[];
}

export const EMPTY_OVERRIDES: Overrides = Object.freeze({ fixed: {}, phased: {}, off: [] }) as Overrides;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const finite = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;
const upperOf = (key: MeasureKey) => (key === "FSCL" ? FSCL_MAX : 1);

export function chosen(o: Overrides, key: MeasureKey, phase: Phase | null = null): Ends | null {
  if (PHASED.includes(key)) return (phase && o.phased[key]?.[phase]) || null;
  return o.fixed[key] ?? null;
}

export function isOff(o: Overrides, key: MeasureKey): boolean {
  return o.off.includes(key);
}

export function emptyOverrides(o: Overrides): boolean {
  return (
    !Object.keys(o.fixed).length && !Object.values(o.phased).some((v) => v && Object.keys(v).length) && !o.off.length
  );
}

/** (ends, null) ou (null, règle violée) : deux nombres finis dans [0, 1] ([0, 1,25] pour la FSCL). */
function readEnds(raw: unknown, key: MeasureKey): [Ends | null, MeasureRefusal | null] {
  if (!Array.isArray(raw) || raw.length !== 2) return [null, "format"];
  const a = finite(raw[0]);
  const b = finite(raw[1]);
  if (a === null || b === null) return [null, "format"];
  const upper = upperOf(key);
  if (!(a >= 0 && a <= upper && b >= 0 && b <= upper)) return [null, "range"];
  return [[a, b], null];
}

/** Lecture tolérante de `placement.measures` (`zo_measures.parse`) : entrées illisibles ou étrangères au niveau
 *  écartées ; un document illisible donne des positions vides. */
export function parseOverrides(raw: unknown, level: MeasureLevel): Overrides {
  const o: Overrides = { fixed: {}, phased: {}, off: [] };
  if (!isRecord(raw) || raw.v !== 1) return o;
  const allowed = storable(level);
  for (const [name, value] of Object.entries(raw)) {
    const key = name as MeasureKey;
    if (name === "v" || !allowed.includes(key) || !isRecord(value)) continue;
    for (const [sub, entry] of Object.entries(value)) {
      if (sub === "off") {
        if (REMOVABLE.includes(key) && entry === true && !o.off.includes(key)) o.off.push(key);
      } else if ((MEASURE_PHASES as readonly string[]).includes(sub)) {
        if (!PHASED.includes(key) || !isRecord(entry) || Object.keys(entry).join() !== "ends") continue;
        const [ends] = readEnds(entry.ends, key);
        if (ends) (o.phased[key] ??= {})[sub as Phase] = ends;
      } else if (sub === "ends" && !PHASED.includes(key)) {
        const [ends] = readEnds(entry, key);
        if (ends) o.fixed[key] = ends;
      }
    }
  }
  return o;
}

/** Forme stockée (`zo_measures.dump`) : `{v: 1, clé: …}` dans l'ordre du catalogue. */
export function dumpOverrides(o: Overrides): PlacementMeasures {
  const out: Record<string, unknown> = { v: 1 };
  for (const key of LINE_KEYS) {
    const entry: Record<string, unknown> = {};
    const fixed = o.fixed[key];
    const phased = o.phased[key];
    const hasPhases = Boolean(phased && Object.keys(phased).length);
    if (REMOVABLE.includes(key) && (o.off.includes(key) || fixed || hasPhases)) entry.off = o.off.includes(key);
    if (fixed) entry.ends = [fixed[0], fixed[1]];
    for (const phase of MEASURE_PHASES) {
      const ends = phased?.[phase];
      if (ends) entry[phase] = { ends: [ends[0], ends[1]] };
    }
    if (Object.keys(entry).length) out[key] = entry;
  }
  return out as unknown as PlacementMeasures;
}

/** Forme stockée, ou null sans aucune position choisie (le serveur retire alors `placement.measures`). */
export function storedOverrides(o: Overrides): PlacementMeasures | null {
  return emptyOverrides(o) ? null : dumpOverrides(o);
}

// --- Écriture d'une ligne (`PUT /measures`, §3.7) -----------------------------------------------------------------

/** Modification demandée (corps de `PUT /measures`) : `ends` défini = position choisie (null : calculée), absent =
 *  inchangée ; `off` : retirer ou rétablir la CFL ou la FSCL. */
export interface RawMeasureChange {
  key: string;
  phase?: string | null;
  ends?: readonly unknown[] | null;
  off?: boolean | null;
}

export type ChangeResult = { ok: true; overrides: Overrides } | { ok: false; rule: MeasureRefusal };

const copyOverrides = (o: Overrides): Overrides => ({
  fixed: { ...o.fixed },
  phased: Object.fromEntries(Object.entries(o.phased).map(([k, v]) => [k, { ...v }])),
  off: [...o.off],
});

/** Positions choisies après la modification, ou la règle de structure violée (`zo_measures.apply_change`). */
export function applyChange(o: Overrides, level: MeasureLevel, change: RawMeasureChange): ChangeResult {
  const key = change.key as MeasureKey;
  const phase = change.phase ?? null;
  const setEnds = change.ends !== undefined;
  const off = change.off ?? null;
  if (BOUNDARIES.includes(key)) return { ok: false, rule: "boundary" };
  if (!storable(level).includes(key)) return { ok: false, rule: "unknown" };
  if (phase !== null && !(MEASURE_PHASES as readonly string[]).includes(phase)) return { ok: false, rule: "format" };
  if (off !== null && !REMOVABLE.includes(key)) return { ok: false, rule: "off" };
  if (!setEnds && off === null) return { ok: false, rule: "format" };
  if (setEnds) {
    if (PHASED.includes(key) && phase === null) return { ok: false, rule: "phase_required" };
    if (!PHASED.includes(key) && phase !== null) return { ok: false, rule: "phase_forbidden" };
  } else if (phase !== null && !PHASED.includes(key)) return { ok: false, rule: "phase_forbidden" };
  const out = copyOverrides(o);
  if (setEnds) {
    if (change.ends === null) {
      if (PHASED.includes(key)) {
        const slots = out.phased[key];
        if (slots) {
          delete slots[phase as Phase];
          if (!Object.keys(slots).length) delete out.phased[key];
        }
      } else delete out.fixed[key];
    } else {
      const [ends, fault] = readEnds(change.ends, key);
      if (fault || !ends) return { ok: false, rule: fault ?? "format" };
      if (PHASED.includes(key)) (out.phased[key] ??= {})[phase as Phase] = ends;
      else out.fixed[key] = ends;
    }
  }
  if (off === true && !out.off.includes(key)) out.off.push(key);
  else if (off === false) out.off = out.off.filter((k) => k !== key);
  return { ok: true, overrides: out };
}

// --- Positions (§3.2) -------------------------------------------------------------------------------------------

export interface DerivedLine {
  key: MeasureKey;
  phase: Phase | null;
  ends: Ends;
  moved: boolean;
  /** Limites internes : règle de la position calculée ; FLOT calculée : null (l'unité n'est connue que du
   *  serveur, `relayout` reprend la sienne). */
  basis: MeasureBasis | null;
}

export interface Derived {
  level: MeasureLevel;
  inputs: MeasureInputs;
  overrides: Overrides;
  /** Ordre du serveur : LIMAR, limites internes, LIMAV, FLOT (T1 à T3), CFL (T1 à T3), FSCL. */
  lines: DerivedLine[];
  absent: MeasureAbsence[];
  /** Conflits dérivés de l'ordre des lignes (les avertissements sur les unités demandent les unités). */
  conflicts: { kind: MeasureWarning; phase: Phase }[];
}

const bound = (value: number, low: number, high: number) => Math.min(Math.max(value, low), high);

/** Position effective d'une ligne (au temps donné pour la FLOT et la CFL), ou null si elle est absente. */
export function effective(d: Pick<Derived, "lines">, key: MeasureKey, phase: Phase | null = null): Ends | null {
  for (const line of d.lines) if (line.key === key && (line.phase === phase || !PHASED.includes(key))) return line.ends;
  return null;
}

/** Positions des lignes de la ZO (`zo_measures.compute`) d'après les entrées du serveur et les positions
 *  choisies : FLOT → CFL → ZOR_ZOP → ZOAR_ZOR → FSCL ; positions choisies prioritaires ; CFL, limites internes et
 *  FSCL calculées bornées par leurs voisines effectives ; FLOT calculée bornée à [0, 1] seulement. */
export function derive(level: MeasureLevel, inputs: MeasureInputs, overrides: Overrides = EMPTY_OVERRIDES): Derived {
  const allowed = storable(level);
  const o: Overrides = {
    fixed: Object.fromEntries(Object.entries(overrides.fixed).filter(([k]) => allowed.includes(k as MeasureKey))),
    phased: Object.fromEntries(Object.entries(overrides.phased).filter(([k]) => allowed.includes(k as MeasureKey))),
    off: overrides.off.filter((k) => allowed.includes(k)),
  };
  const { gap: g, cfl_gap: delta, zone_min: z, rear_margin: margin } = inputs;
  const hasFscl = LEVEL_LINES[level].includes("FSCL");
  const fsclChosen = hasFscl && !isOff(o, "FSCL") ? (o.fixed.FSCL ?? null) : null;
  const lines: DerivedLine[] = [{ key: "LIMAR", phase: null, ends: [0, 0], moved: false, basis: null }];
  const absent: MeasureAbsence[] = [];
  const conflicts: { kind: MeasureWarning; phase: Phase }[] = [];
  const flot: Partial<Record<Phase, Ends | null>> = {};
  const cfl: Partial<Record<Phase, Ends | null>> = {};
  const flotLines: DerivedLine[] = [];
  const cflLines: DerivedLine[] = [];

  for (const phase of MEASURE_PHASES) {
    const computed = inputs.flot_computed?.[phase] ?? null;
    const flotChosen = chosen(o, "FLOT", phase);
    let ends: Ends | null = null;
    if (flotChosen) ends = flotChosen;
    else if (computed !== null) ends = [computed, computed];
    else absent.push({ key: "FLOT", phase, reason: "no_unit" });
    flot[phase] = ends;
    if (ends) flotLines.push({ key: "FLOT", phase, ends, moved: flotChosen !== null, basis: null });
    // CFL.
    if (isOff(o, "CFL")) {
      cfl[phase] = null;
      absent.push({ key: "CFL", phase, reason: "off" });
      continue;
    }
    const cflChosen = chosen(o, "CFL", phase);
    if (cflChosen) {
      cfl[phase] = cflChosen;
      cflLines.push({ key: "CFL", phase, ends: cflChosen, moved: true, basis: null });
      continue;
    }
    if (!ends) {
      cfl[phase] = null;
      absent.push({ key: "CFL", phase, reason: "no_unit" });
      continue;
    }
    const values = [0, 1].map((side) => {
      let c = Math.min(ends[side]! + delta, 1);
      if (fsclChosen) c = Math.min(c, fsclChosen[side]! - g);
      return c;
    }) as [number, number];
    if (values.some((c, side) => c - ends[side]! < g)) {
      cfl[phase] = null;
      absent.push({ key: "CFL", phase, reason: "forward_boundary" });
      continue;
    }
    cfl[phase] = values;
    cflLines.push({ key: "CFL", phase, ends: values, moved: false, basis: null });
  }

  // Limites internes.
  const internal: Partial<Record<MeasureKey, DerivedLine>> = {};
  if (P2[level] !== undefined) internal.ZOR_ZOP = zorZop(level, inputs, o, flot);
  if (P1[level] !== undefined) {
    internal.ZOAR_ZOR = zoarZor(level, inputs, o, internal.ZOR_ZOP!, g, z, margin);
    // ZOR_ZOP calculée : bornée au-delà d'une ZOAR_ZOR choisie (voisine inférieure + g).
    const zop = internal.ZOR_ZOP!;
    const zoar = internal.ZOAR_ZOR;
    if (!zop.moved && zoar.moved) {
      const tight = [0, 1].map((side) => bound(zop.ends[side]!, zoar.ends[side]! + g, 1 - g)) as [number, number];
      if (tight[0] !== zop.ends[0] || tight[1] !== zop.ends[1])
        internal.ZOR_ZOP = { key: "ZOR_ZOP", phase: null, ends: tight, moved: false, basis: { rule: "neighbour" } };
    }
  }
  for (const key of INTERNAL) if (internal[key]) lines.push(internal[key]!);
  lines.push({ key: "LIMAV", phase: null, ends: [1, 1], moved: false, basis: null });
  lines.push(...flotLines, ...cflLines);

  // FSCL (division).
  if (hasFscl) {
    if (isOff(o, "FSCL")) absent.push({ key: "FSCL", phase: null, reason: "off" });
    else if (fsclChosen) lines.push({ key: "FSCL", phase: null, ends: fsclChosen, moved: true, basis: null });
    else {
      const s = inputs.fscl ?? FSCL_BY_LEVEL[level] ?? FSCL_S;
      lines.push({ key: "FSCL", phase: null, ends: [s, s], moved: false, basis: null });
    }
  }

  // Conflits dérivés (non bloquants), à moins de `g` sur un côté.
  const fsclEff = effective({ lines }, "FSCL");
  for (const phase of MEASURE_PHASES) {
    const f = flot[phase];
    const c = cfl[phase];
    if (f && c && [0, 1].some((s) => f[s]! > c[s]! - g + EPS)) conflicts.push({ kind: "flot_beyond_cfl", phase });
    if (f && fsclEff && [0, 1].some((s) => f[s]! > fsclEff[s]! - g + EPS))
      conflicts.push({ kind: "fscl_short_of_flot", phase });
  }
  return { level, inputs, overrides: o, lines, absent, conflicts };
}

/** ZOR_ZOP : v = max(p₂, F* + Δ + g) (p₂ sans FLOT), F* = plus avancée des FLOT effectives de T1 à T3 (deux
 *  côtés), bornée à [2z, 1 − z] (division) ou [z, 1 − z] (brigade). */
function zorZop(
  level: MeasureLevel,
  inputs: MeasureInputs,
  o: Overrides,
  flot: Partial<Record<Phase, Ends | null>>,
): DerivedLine {
  const picked = o.fixed.ZOR_ZOP;
  if (picked) return { key: "ZOR_ZOP", phase: null, ends: picked, moved: true, basis: null };
  const { gap: g, cfl_gap: delta, zone_min: z } = inputs;
  const p2 = inputs.p2 ?? P2[level]!;
  let value = p2;
  let basis: MeasureBasis = { rule: "proportion" };
  let top: { s: number; phase: Phase } | null = null;
  for (const phase of MEASURE_PHASES) {
    const ends = flot[phase];
    if (!ends) continue;
    const s = Math.max(ends[0], ends[1]);
    if (!top || s > top.s) top = { s, phase }; // égalité : le premier temps
  }
  if (top && top.s + delta + g > p2) {
    value = top.s + delta + g;
    basis = { rule: "flot", phase: top.phase };
  }
  const bounded = bound(value, P1[level] !== undefined ? 2 * z : z, 1 - z);
  if (bounded !== value) basis = { rule: "min_depth" };
  return { key: "ZOR_ZOP", phase: null, ends: [bounded, bounded], moved: false, basis };
}

/** ZOAR_ZOR : u = min(p₁, S* − m) (p₁ sans unité de manœuvre), bornée à [z, ZOR_ZOP − z] côté par côté, puis, en
 *  priorité, à [g, ZOR_ZOP − g]. */
function zoarZor(level: MeasureLevel, inputs: MeasureInputs, o: Overrides, zop: DerivedLine, g: number, z: number, margin: number): DerivedLine {
  const picked = o.fixed.ZOAR_ZOR;
  if (picked) return { key: "ZOAR_ZOR", phase: null, ends: picked, moved: true, basis: null };
  const p1 = inputs.p1 ?? P1[level]!;
  let value = p1;
  let basis: MeasureBasis = { rule: "proportion" };
  const lowest = inputs.maneuver_min_s;
  if (lowest !== null && lowest - margin < p1) {
    value = lowest - margin;
    basis = { rule: "maneuver" };
  }
  const rules = new Set<"neighbour" | "min_depth">();
  const ends = [0, 1].map((side) => {
    const high = zop.ends[side]! - z;
    let bounded = bound(value, z, high);
    if (bounded !== value) rules.add(zop.moved && bounded === high ? "neighbour" : "min_depth");
    const tight = bound(bounded, g, zop.ends[side]! - g);
    if (tight !== bounded) {
      bounded = tight;
      rules.add("neighbour");
    }
    return bounded;
  }) as [number, number];
  if (rules.size) basis = { rule: rules.has("neighbour") ? "neighbour" : "min_depth" };
  return { key: "ZOAR_ZOR", phase: null, ends, moved: false, basis };
}

/** Zones du niveau : lignes qui les bornent, abscisses moyennes, profondeur (fraction de l'axe). */
export function zoneSpans(
  d: Derived,
): { key: MeasureZoneKey; between: [MeasureKey, MeasureKey]; low: Ends; high: Ends; mid: number; depth: number }[] {
  const out = [];
  for (const [key, between] of Object.entries(LEVEL_ZONES[d.level]) as [MeasureZoneKey, [MeasureKey, MeasureKey]][]) {
    const low = effective(d, between[0]);
    const high = effective(d, between[1]);
    if (!low || !high) continue;
    const a = (low[0] + low[1]) / 2;
    const b = (high[0] + high[1]) / 2;
    out.push({ key, between, low, high, mid: (a + b) / 2, depth: b - a });
  }
  return out;
}

// --- Contrôles d'ordre (§3.5) -----------------------------------------------------------------------------------

/** Règle 1 : limites internes choisies ordonnées entre 0 et 1 à `g` près, côté par côté (une limite calculée
 *  entre deux garde sa place) ; `only` : ne contrôle que cette limite. */
function internalChainError(level: MeasureLevel, o: Overrides, g: number, only: MeasureKey | null = null): boolean {
  const keys = INTERNAL.filter((k) => storable(level).includes(k));
  return keys.some((key, index) => {
    const ends = o.fixed[key];
    if (!ends || (only !== null && key !== only)) return false;
    return [0, 1].some((side) => {
      const [low, below] = chainNeighbour(keys, o, index, side, -1);
      const [high, above] = chainNeighbour(keys, o, index, side, 1);
      return ends[side]! < low + below * g - CHAIN_EPS || ends[side]! > high - above * g + CHAIN_EPS;
    });
  });
}

/** Voisine choisie la plus proche dans la chaîne des limites internes (sens −1 : arrière, +1 : avant), et nombre
 *  d'écarts `g` à garder (une limite calculée intermédiaire garde sa place) ; à défaut, l'extrémité du fuseau. */
function chainNeighbour(
  keys: readonly MeasureKey[],
  o: Overrides,
  index: number,
  side: number,
  direction: -1 | 1,
): [value: number, steps: number, key: MeasureKey] {
  for (let j = index + direction; j >= 0 && j < keys.length; j += direction) {
    const other = o.fixed[keys[j]!];
    if (other) return [other[side]!, Math.abs(j - index), keys[j]!];
  }
  return direction < 0 ? [0, index + 1, "LIMAR"] : [1, keys.length - index, "LIMAV"];
}

/** Règles bloquantes de `PUT /measures` (`zo_measures.order_error`), limitées à la ligne modifiée, côté par côté,
 *  sur les positions choisies après la modification (`after`) : règle violée (`order`) ou null. */
export function orderError(
  level: MeasureLevel,
  inputs: MeasureInputs,
  after: Overrides,
  change: Pick<RawMeasureChange, "key" | "phase" | "ends">,
): MeasureRefusal | null {
  const d = derive(level, inputs, after);
  const o = d.overrides;
  const g = inputs.gap;
  const key = change.key as MeasureKey;
  if (INTERNAL.includes(key)) return internalChainError(level, o, g, key) ? "order" : null;
  const fscl = !isOff(o, "FSCL") && storable(level).includes("FSCL") ? (o.fixed.FSCL ?? null) : null;
  if (PHASED.includes(key)) {
    if (key === "CFL" && isOff(o, "CFL")) return null;
    const phases = change.ends !== undefined && change.phase ? [change.phase as Phase] : MEASURE_PHASES;
    for (const phase of phases) {
      const ends = chosen(o, key, phase);
      if (!ends) continue;
      for (const side of [0, 1]) {
        const value = ends[side]!;
        if (key === "FLOT") {
          const c = isOff(o, "CFL") ? null : chosen(o, "CFL", phase);
          if (!(value >= 0 && value <= 1)) return "order";
          if (c && !(value < c[side]! - g)) return "order";
        } else {
          const f = effective(d, "FLOT", phase);
          if (f && !(value > f[side]! + g)) return "order";
          if (value > 1) return "order";
        }
        if (fscl && !(value < fscl[side]! - g)) return "order";
      }
    }
    return null;
  }
  if (key === "FSCL" && fscl) {
    const cflOff = isOff(o, "CFL");
    for (const side of [0, 1]) {
      if (fscl[side]! > FSCL_MAX) return "order";
      for (const phase of MEASURE_PHASES) {
        for (const ends of [effective(d, "FLOT", phase), cflOff ? null : chosen(o, "CFL", phase)]) {
          if (ends && !(fscl[side]! > ends[side]! + g)) return "order";
        }
      }
    }
  }
  return null;
}

/** Refus d'une écriture tel que le serveur le donnerait (structure, puis ordre) : règle violée, ou null si elle
 *  passe. Contrôle préalable de la carte ; le serveur fait foi. */
export function writeError(
  level: MeasureLevel,
  inputs: MeasureInputs,
  current: Overrides,
  change: RawMeasureChange,
): MeasureRefusal | null {
  const result = applyChange(current, level, change);
  if (!result.ok) return result.rule;
  return orderError(level, inputs, result.overrides, change);
}

// --- Butées d'un glissement (§4.3) -------------------------------------------------------------------------------

/** Ligne qui arrête un glissement (temps compris : une voisine à un autre temps désigne ce temps) ; null : borne
 *  du fuseau sans tracé (FSCL à 25 % au-delà de la limite avant). */
export interface Neighbour {
  key: MeasureKey;
  phase: Phase | null;
}

interface Bound {
  /** Borne de chaque côté (abscisse). */
  value: [number, number];
  /** Inégalité stricte du serveur (`<`, `>`) : la position atteinte exactement est refusée. */
  strict: boolean;
  neighbour: Neighbour | null;
}

/** Bornes d'une ligne glissée (règles bloquantes du §3.5 et structure du §3.4), relevées sur les positions choisies
 *  d'avant le glissement : la ligne glissée devient choisie, ses voisines calculées s'adaptent, sauf la FLOT
 *  calculée (CFL et FSCL) ; les voisines choisies l'arrêtent à tous les temps. */
function slideBounds(
  level: MeasureLevel,
  inputs: MeasureInputs,
  o: Overrides,
  key: MeasureKey,
  phase: Phase | null,
): { lower: Bound[]; upper: Bound[] } {
  const d = derive(level, inputs, o);
  const g = inputs.gap;
  const lower: Bound[] = [];
  const upper: Bound[] = [];
  const both = (v: number): [number, number] => [v, v];
  const shifted = (ends: Ends, by: number): [number, number] => [ends[0] + by, ends[1] + by];
  const fscl = !isOff(o, "FSCL") && storable(level).includes("FSCL") ? (o.fixed.FSCL ?? null) : null;
  const limar: Neighbour = { key: "LIMAR", phase: null };
  const limav: Neighbour = { key: "LIMAV", phase: null };
  if (INTERNAL.includes(key)) {
    const keys = INTERNAL.filter((k) => storable(level).includes(k));
    const index = keys.indexOf(key);
    for (const direction of [-1, 1] as const) {
      // Même voisine des deux côtés : la plus proche limite choisie, sinon l'extrémité du fuseau.
      const sides = [0, 1].map((side) => chainNeighbour(keys, o, index, side, direction));
      const value = sides.map(([v, steps]) => v - direction * steps * g) as [number, number];
      (direction < 0 ? lower : upper).push({ value, strict: false, neighbour: { key: sides[0]![2], phase: null } });
    }
    return { lower, upper };
  }
  if (key === "FLOT" && phase) {
    const c = isOff(o, "CFL") ? null : chosen(o, "CFL", phase);
    if (c) upper.push({ value: shifted(c, -g), strict: true, neighbour: { key: "CFL", phase } });
    if (fscl) upper.push({ value: shifted(fscl, -g), strict: true, neighbour: { key: "FSCL", phase: null } });
    upper.push({ value: both(1), strict: false, neighbour: limav });
    lower.push({ value: both(0), strict: false, neighbour: limar });
  } else if (key === "CFL" && phase) {
    const f = effective(d, "FLOT", phase);
    if (f) lower.push({ value: shifted(f, g), strict: true, neighbour: { key: "FLOT", phase } });
    if (fscl) upper.push({ value: shifted(fscl, -g), strict: true, neighbour: { key: "FSCL", phase: null } });
    upper.push({ value: both(1), strict: false, neighbour: limav });
    lower.push({ value: both(0), strict: false, neighbour: limar });
  } else if (key === "FSCL") {
    // FLOT effectives et CFL choisies de tous les temps (règle 4) ; une CFL calculée s'adapte.
    for (const p of MEASURE_PHASES) {
      const f = effective(d, "FLOT", p);
      if (f) lower.push({ value: shifted(f, g), strict: true, neighbour: { key: "FLOT", phase: p } });
      const c = isOff(o, "CFL") ? null : chosen(o, "CFL", p);
      if (c) lower.push({ value: shifted(c, g), strict: true, neighbour: { key: "CFL", phase: p } });
    }
    upper.push({ value: both(FSCL_MAX), strict: false, neighbour: null });
    lower.push({ value: both(0), strict: false, neighbour: limar });
  }
  return { lower, upper };
}

/** Butée d'un sens : déplacement signé extrême admis, voisine qui arrête, inégalité stricte. */
export interface SlideStop {
  delta: number;
  strict: boolean;
  neighbour: Neighbour | null;
}

/** Glissement admis d'une ligne : position de départ (effective) et butées des deux sens, en Δs signé appliqué aux
 *  côtés retenus.
 *  - `empty` : aucune position admise ; la ligne reste à sa place. Soit `min.delta > max.delta` (ordre déjà rompu
 *    des deux côtés de la plage par des positions choisies), soit, pour une poignée d'extrémité, le côté opposé
 *    enfreint déjà une règle (`held`).
 *  - `held` : butée que le côté opposé a déjà franchie (poignée d'extrémité) ; la ligne figée la désigne, quel que
 *    soit le sens tiré. Null sinon.
 *  Le départ peut aussi être hors de la plage sans qu'elle soit vide (ordre rompu par le dispositif, docs/15 §3.2 :
 *  FLOT calculée au-delà d'une CFL choisie) : `clampSlide` ne laisse alors la ligne aller que vers sa place admise. */
export interface SlideRange {
  start: Ends;
  min: SlideStop;
  max: SlideStop;
  empty: boolean;
  held: SlideStop | null;
}

function rangeOf(start: Ends, bounds: { lower: Bound[]; upper: Bound[] }, sides: readonly number[]): SlideRange {
  let min: SlideStop = { delta: -Infinity, strict: false, neighbour: null };
  let max: SlideStop = { delta: Infinity, strict: false, neighbour: null };
  for (const b of bounds.lower) {
    for (const side of sides) {
      const delta = b.value[side]! - start[side]!;
      if (delta > min.delta) min = { delta, strict: b.strict, neighbour: b.neighbour };
    }
  }
  for (const b of bounds.upper) {
    for (const side of sides) {
      const delta = b.value[side]! - start[side]!;
      if (delta < max.delta) max = { delta, strict: b.strict, neighbour: b.neighbour };
    }
  }
  return { start, min, max, empty: min.delta > max.delta, held: null };
}

/** Le déplacement `delta` respecte la butée (inégalité stricte du serveur, ou large à `CHAIN_EPS` près). */
function belowStop(stop: SlideStop, delta: number): boolean {
  return stop.strict ? delta < stop.delta : delta <= stop.delta + CHAIN_EPS;
}
function aboveStop(stop: SlideStop, delta: number): boolean {
  return stop.strict ? delta > stop.delta : delta >= stop.delta - CHAIN_EPS;
}

/** Δs admis par les deux butées (position que le serveur accepterait). */
export function admits(range: SlideRange, delta: number): boolean {
  return !range.empty && belowStop(range.max, delta) && aboveStop(range.min, delta);
}

/** Butées d'un glissement uniforme (même Δs aux deux extrémités) de la ligne `key` (au temps `phase` pour la FLOT et
 *  la CFL), depuis sa position effective : `max` vers l'avant, `min` vers l'arrière. Null pour une ligne absente,
 *  LIMAR et LIMAV (elles suivent le fuseau). Bornes du serveur au plus près (vecteurs `stops` et `escapes`). */
export function slideRange(
  level: MeasureLevel,
  inputs: MeasureInputs,
  overrides: Overrides,
  key: MeasureKey,
  phase: Phase | null = null,
): SlideRange | null {
  if (BOUNDARIES.includes(key) || !storable(level).includes(key)) return null;
  const linePhase = PHASED.includes(key) ? phase : null;
  if (PHASED.includes(key) && !linePhase) return null;
  const start = effective(derive(level, inputs, overrides), key, linePhase);
  if (!start) return null;
  return rangeOf(start, slideBounds(level, inputs, overrides, key, linePhase), [0, 1]);
}

/** Butées d'une seule extrémité (`side` : 0 = limite gauche, 1 = droite ; poignée d'extrémité, la ligne s'incline).
 *  Le serveur contrôle les deux côtés de la ligne écrite : si le côté opposé, qui ne bouge pas, enfreint déjà une
 *  règle, aucune position n'est admise (`empty`) et la ligne figée désigne la butée franchie (`held`). Vecteurs
 *  `tilts`. */
export function endRange(
  level: MeasureLevel,
  inputs: MeasureInputs,
  overrides: Overrides,
  key: MeasureKey,
  phase: Phase | null,
  side: 0 | 1,
): SlideRange | null {
  const range = slideRange(level, inputs, overrides, key, phase);
  if (!range) return null;
  const linePhase = PHASED.includes(key) ? phase : null;
  const bounds = slideBounds(level, inputs, overrides, key, linePhase);
  const own = rangeOf(range.start, bounds, [side]);
  const opposite = rangeOf(range.start, bounds, [1 - side]);
  const held = !belowStop(opposite.max, 0) ? opposite.max : !aboveStop(opposite.min, 0) ? opposite.min : null;
  return held ? { ...own, empty: true, held } : own;
}

/** Marge gardée en deçà d'une butée (fraction de l'axe, 0,1 mm sur 100 km) : la position écrite reste admise malgré
 *  les arrondis et les inégalités strictes du serveur. */
export const SLIDE_MARGIN = 1e-9;

/** Glissement ramené dans les butées.
 *  - `delta` : Δs à afficher ; `stop` : voisine à désigner (atteinte, ou enfreinte) ; `stopped` : la ligne est
 *    retenue (elle ne suit plus le pointeur) ; `admitted` : le serveur accepterait la position (sinon, au relâcher,
 *    rien n'est écrit et la ligne revient à sa place).
 *  - Sans position admise (`empty`), la ligne reste au départ (Δs = 0) et désigne la butée franchie (`held`), sinon
 *    celle du sens demandé.
 *  - Départ hors de la plage (non vide) : la ligne ne s'éloigne pas davantage (Δs = 0, butée enfreinte désignée) ;
 *    vers sa place admise, elle suit le pointeur, sans saut, en désignant la butée enfreinte tant qu'elle ne l'a pas
 *    repassée, puis s'arrête à la butée opposée comme d'habitude. */
export function clampSlide(
  range: SlideRange,
  delta: number,
): { delta: number; stop: Neighbour | null; stopped: boolean; admitted: boolean } {
  if (range.empty) {
    const stop = range.held ?? (delta >= 0 ? range.max : range.min);
    return { delta: 0, stop: stop.neighbour, stopped: true, admitted: false };
  }
  const high = range.max.delta - SLIDE_MARGIN;
  const low = range.min.delta + SLIDE_MARGIN;
  const fault = !belowStop(range.max, 0) ? range.max : !aboveStop(range.min, 0) ? range.min : null;
  if (fault) {
    const ahead = fault === range.max; // départ au-delà de la butée avant : la place admise est en arrière
    if (ahead ? delta >= 0 : delta <= 0) return { delta: 0, stop: fault.neighbour, stopped: true, admitted: false };
    if (ahead ? delta > high : delta < low) return { delta, stop: fault.neighbour, stopped: false, admitted: false };
  }
  if (delta > high) {
    const d = Math.max(high, low);
    return { delta: d, stop: range.max.neighbour, stopped: true, admitted: admits(range, d) };
  }
  if (delta < low) {
    const d = Math.min(low, high);
    return { delta: d, stop: range.min.neighbour, stopped: true, admitted: admits(range, d) };
  }
  return { delta, stop: null, stopped: false, admitted: true };
}

// --- Géométrie (§3.3) --------------------------------------------------------------------------------------------

/** Point au-delà de la limite avant (s > 1) : depuis point(1, t), dans la direction de l'axe sur ses 10 % finaux
 *  (C(0,9) → C(1)), de (s − 1) × longueur de l'axe (`zo_measures.extrapolate`, rayon de 6 378 137 m). */
export function extrapolate(frame: CorridorFrame, s: number, t: number): LonLat {
  const start = frame.point(1, t);
  const direction = bearingDeg(frame.point(EXTRAPOLATION_FROM, 0), frame.point(1, 0));
  return moveM(start, direction, (s - 1) * frame.axisLength);
}

/** Tracé d'une ligne : 17 points [lon, lat] de la limite gauche (t = −1) à la droite (t = +1), extrémités exactement
 *  sur les limites ; au-delà de la limite avant, extrapolation. */
export function lineCoords(frame: CorridorFrame, ends: Ends): LonLat[] {
  const out: LonLat[] = [];
  for (let k = 0; k <= SAMPLES; k++) {
    const t = -1 + (2 * k) / SAMPLES;
    const s = ends[0] + ((ends[1] - ends[0]) * k) / SAMPLES;
    out.push(s <= 1 ? frame.point(s, t) : extrapolate(frame, s, t));
  }
  return out;
}

/** Calque recalculé côté client (`MeasuresOut`) pour un aperçu : repère de travail `frame` (fuseau en cours de
 *  geste, ou celui du serveur) et positions choisies `overrides` (par défaut celles du serveur). Les fractions qui
 *  dépendent de la longueur de l'axe sont recalculées ; les abscisses des unités (FLOT calculée, unité de manœuvre
 *  la plus en arrière) sont gardées, le transport des unités conservant (s, t). Unité de la FLOT calculée et
 *  avertissements sur les unités : ceux du serveur ; noms : ceux du serveur, sinon ceux du catalogue (`NAMES`). */
export function relayout(
  measures: Measures,
  level: MeasureLevel,
  frame: CorridorFrame,
  overrides: Overrides = parseOverrides(measures.overrides, level),
): Measures {
  const inputs: MeasureInputs = { ...measures.inputs, ...fractionsOf(frame.axisLength) };
  const d = derive(level, inputs, overrides);
  // Noms du serveur ; à défaut (ligne qu'il n'a pas renvoyée : CFL ou FSCL rétablie), ceux du catalogue du niveau.
  const lineNames = new Map<string, MeasureNames>();
  for (const line of measures.lines) if (!lineNames.has(line.key)) lineNames.set(line.key, line.names);
  const zoneNames = new Map<string, MeasureNames>(measures.zones.map((z) => [z.key, z.names]));
  const catalogue = NAMES[level];
  const lines: MeasureLine[] = d.lines.map((line) => {
    let basis = line.basis;
    if (line.key === "FLOT" && !line.moved) {
      const server = measures.lines.find((l) => l.key === "FLOT" && l.phase === line.phase && !l.moved);
      basis = server?.basis ?? null;
    }
    return {
      key: line.key,
      kind: KINDS[line.key],
      phase: line.phase,
      ends: [line.ends[0], line.ends[1]],
      coordinates: lineCoords(frame, line.ends),
      style: STYLES[line.key].style,
      weight: STYLES[line.key].weight,
      movable: !BOUNDARIES.includes(line.key),
      moved: line.moved,
      basis,
      names: lineNames.get(line.key) ?? catalogue[line.key]!,
    };
  });
  const zones: MeasureZone[] = [];
  const depths: Partial<Record<MeasureZoneKey, number>> = {};
  for (const span of zoneSpans(d)) {
    const label = frame.point(span.mid, 0);
    zones.push({
      key: span.key,
      between: span.between,
      label: [label[0], label[1]],
      names: zoneNames.get(span.key) ?? catalogue[span.key]!,
    });
    depths[span.key] = span.depth;
  }
  // Avertissements : conflits de l'ordre recalculés, avertissements sur les unités repris du serveur.
  const found = d.conflicts.map((c) => ({ ...c }));
  for (const text of measures.warnings) {
    for (const kind of ["units_beyond_flot", "units_beyond_limit"] as const) {
      const phase = MEASURE_PHASES.find((p) => warningText(kind, p) === text);
      if (phase) found.push({ kind, phase });
    }
  }
  found.sort(
    (a, b) => MEASURE_PHASES.indexOf(a.phase) - MEASURE_PHASES.indexOf(b.phase) || WARNING_KINDS.indexOf(a.kind) - WARNING_KINDS.indexOf(b.kind),
  );
  return {
    v: measures.v,
    lines,
    zones,
    absent: d.absent,
    cfl_off: isOff(d.overrides, "CFL"),
    fscl_off: isOff(d.overrides, "FSCL"),
    overrides: dumpOverrides(d.overrides),
    inputs,
    axis_length_m: frame.axisLength,
    depth_fractions: depths,
    warnings: found.map((w) => warningText(w.kind, w.phase)),
  };
}

/** Lignes du temps affiché (FLOT et CFL de ce temps, les autres lignes toujours). */
export function linesAt<T extends { key: MeasureKey; phase: Phase | null }>(lines: readonly T[], phase: Phase): T[] {
  return lines.filter((line) => line.phase === null || line.phase === phase);
}

// --- Puces : anti-recouvrement (§4.1, amendé le 02/10/2026) -------------------------------------------------------

/** Hauteur d'une puce (h-5 : puces des LIMA et des lignes de la ZO). */
export const TAG_HEIGHT_PX = 20;
/** Écart entre le point d'ancrage et le bord de la puce, dans l'axe de la ligne (`corridorDisplay.LABEL_GAP_PX`). */
export const TAG_GAP_PX = 4;
/** Marge minimale entre deux boîtes (puce, marque d'échelon) ou entre une boîte et un trait : le glissement d'une puce
 *  qui recouvre est le minimum qui la dégage, plus cette marge. */
export const TAG_MARGIN_PX = 4;
/** Boîte d'une marque d'échelon (II, X, XX en 28 px, tournée avec la limite) : carré centré sur son point. */
export const MARK_BOX_PX = 28;
/** Glissement maximal d'une puce au-delà du bout de sa ligne (px) : faute de place, elle s'arrête là. */
export const TAG_MAX_SHIFT_PX = 240;
const TAG_MAX_STEPS = 160;
/** Pas du dégagement d'un trait (limite du fuseau, autre ligne) : la boîte avance de 2 px jusqu'à ne plus le toucher. */
const STROKE_STEP_PX = 2;
/** Course maximale du dégagement d'un trait (px), au-delà de la place où la puce ne recouvre plus aucune boîte : faute
 *  de place libre plus près, la puce reste à cette place et effleure le trait, plutôt que de s'éloigner du bout de sa
 *  ligne au point qu'on ne sache plus à quelle ligne elle appartient (fuseau serré aux zooms d'iPad portrait). */
export const TAG_STROKE_MAX_PX = 24;
/** Variante décalée d'une puce (`lateral`) : le bout de sa ligne reste à cette distance (px) du bord de la boîte. */
export const TAG_LATERAL_INSET_PX = 8;
/** Nombre maximal de combinaisons de variantes explorées (`lateral`) ; au-delà, choix variante par variante. */
export const TAG_SEARCH_MAX = 1024;

/** Largeur estimée d'une puce (px) : nom en 12 px demi-gras, détail éventuel (GDH des LIMA) en chasse fixe 11 px,
 *  marges intérieures et bordure. Estimation par excès plutôt que mesure du DOM : pas d'image de retard ni de
 *  saut au premier affichage ; la marge `TAG_MARGIN_PX` couvre l'écart avec la police réelle. */
export function tagWidthPx(name: string, detail: string | null = null): number {
  let width = 14; // px-1.5 de chaque côté + bordure
  for (const ch of name) {
    if (ch === " ") width += 3.5;
    else if (/[A-Z0-9ÀÂÉÈÊËÎÏÔÙÛÜÇ]/.test(ch)) width += 8.2;
    else if (/[a-zàâéèêëîïôùûüç]/.test(ch)) width += 6.8;
    else width += 4.5;
  }
  if (detail) width += 6 + [...detail].length * 6.7; // gap-1.5 + chasse fixe (0,6 em)
  return Math.ceil(width);
}

/** Boîte à l'écran (pixels, y vers le bas), alignée sur les axes. */
export interface ScreenBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface TagRequest {
  id: string;
  /** Tracé de la ligne tel qu'il est dessiné, de la gauche à la droite : la puce se pose au-delà du dernier point. */
  line: readonly LonLat[];
  /** Largeur estimée de la puce (`tagWidthPx`). */
  widthPx: number;
}

/** Obstacle des puces : marque d'échelon (point, boîte carrée `sizePx`) ou puce déjà posée par un autre calque (puce
 *  de LIMA : `line` finit à son point d'ancrage, dans l'axe de sa ligne — `[from, at]` d'un `PlacedTag` —, largeur
 *  estimée). */
export type TagObstacle =
  | { at: LonLat; sizePx?: number; heightPx?: number }
  | { line: readonly LonLat[]; widthPx: number; offsetPx?: readonly [number, number] };

/** Trait qu'une puce ne doit pas toucher : limite du fuseau, autre ligne. `id` : celui de la puce de cette ligne (une
 *  puce ignore sa propre ligne, qu'elle prolonge). */
export interface TagStroke {
  id?: string;
  line: readonly LonLat[];
}

export interface PlacedTag {
  id: string;
  /** Point d'ancrage de la puce : dernier point de la ligne, prolongé de `shiftPx` dans l'axe de son dernier
   *  segment (au-delà du bout : le dépassement de la ligne reste visible). */
  at: LonLat;
  /** Point de la ligne qui donne la direction de son bout (`from` → `at`) : obstacle pour un autre calque. */
  from: LonLat;
  /** Glissement vers l'extérieur depuis le dernier point, en pixels dans l'axe de la ligne. */
  shiftPx: number;
  /** Décalage à l'écran (px) de la variante retenue (`lateral`), à ajouter au rendu de la puce : [0, 0] centrée. */
  offsetPx: readonly [number, number];
  /** Cap (°) du dernier segment de la ligne, vers son bout (`labelPlacement`). */
  bearing: number;
  /** Largeur estimée de la puce. */
  widthPx: number;
  /** Boîte occupée à l'écran (calculée comme `labelPlacement` + `labelTransform`). */
  box: ScreenBox;
}

/** Boîte d'une puce ancrée en `at` (écran) sur une ligne de direction `u` (unitaire, vers son dernier point) : comme
 *  `labelPlacement` + `labelTransform`, son bord passe à `TAG_GAP_PX` au-delà du point d'ancrage dans l'axe de la
 *  ligne, et elle s'étend du côté opposé à la ligne. */
export function tagBox(at: readonly [number, number], u: readonly [number, number], widthPx: number, heightPx = TAG_HEIGHT_PX): ScreenBox {
  const m = Math.max(Math.abs(u[0]), Math.abs(u[1])) || 1;
  const cx = at[0] + u[0] * TAG_GAP_PX + (u[0] / m) * (widthPx / 2);
  const cy = at[1] + u[1] * TAG_GAP_PX + (u[1] / m) * (heightPx / 2);
  return { x0: cx - widthPx / 2, y0: cy - heightPx / 2, x1: cx + widthPx / 2, y1: cy + heightPx / 2 };
}

/** Les deux boîtes se recouvrent-elles, marge comprise ? */
export function boxesOverlap(a: ScreenBox, b: ScreenBox, marginPx = TAG_MARGIN_PX): boolean {
  return a.x0 < b.x1 + marginPx && b.x0 < a.x1 + marginPx && a.y0 < b.y1 + marginPx && b.y0 < a.y1 + marginPx;
}

/** Déplacement le long de `v` (unitaire) qui dégage la boîte `a` de `b`, marge comprise (0 si déjà dégagée) : le plus
 *  court des deux axes. */
export function clearance(a: ScreenBox, b: ScreenBox, v: readonly [number, number], marginPx = TAG_MARGIN_PX): number {
  if (!boxesOverlap(a, b, marginPx)) return 0;
  let best = Infinity;
  if (Math.abs(v[0]) > 1e-9) best = Math.min(best, (v[0] < 0 ? a.x1 - (b.x0 - marginPx) : b.x1 + marginPx - a.x0) / Math.abs(v[0]));
  if (Math.abs(v[1]) > 1e-9) best = Math.min(best, (v[1] < 0 ? a.y1 - (b.y0 - marginPx) : b.y1 + marginPx - a.y0) / Math.abs(v[1]));
  return best;
}

/** Le segment [a, b] (écran) entre-t-il dans la boîte élargie de la marge ? (découpage de Liang-Barsky ; un segment
 *  qui ne fait qu'effleurer le bord de la marge ne compte pas : deux LIMA voisines finies à la même hauteur ne
 *  repoussent pas la puce l'une de l'autre.) */
export function segmentHitsBox(
  a: readonly [number, number],
  b: readonly [number, number],
  box: ScreenBox,
  marginPx = TAG_MARGIN_PX,
): boolean {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const p = [-dx, dx, -dy, dy];
  const q = [a[0] - (box.x0 - marginPx), box.x1 + marginPx - a[0], a[1] - (box.y0 - marginPx), box.y1 + marginPx - a[1]];
  let t0 = 0;
  let t1 = 1;
  for (let i = 0; i < 4; i++) {
    const pi = p[i]!;
    const qi = q[i]!;
    if (Math.abs(pi) < 1e-12) {
      if (qi <= 0) return false;
      continue;
    }
    const r = qi / pi;
    if (pi < 0) {
      if (r > t1) return false;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return false;
      if (r < t1) t1 = r;
    }
  }
  return t0 < t1 - 1e-9;
}

/** Bout d'une ligne à l'écran : dernier point, direction unitaire de son dernier segment (vers le bout) et point de
 *  départ de ce segment — le premier en arrière à au moins 1 px, sinon le plus éloigné ; null sans deux points
 *  distincts. */
function lineEnd(
  line: readonly LonLat[],
  project: (p: LonLat) => readonly [number, number],
): { tip: [number, number]; u: [number, number]; last: LonLat; from: LonLat; spanPx: number } | null {
  const n = line.length;
  if (n === 0) return null;
  const last = line[n - 1]!;
  const tip = project(last);
  let from: LonLat | null = null;
  let span = 0;
  let u: [number, number] = [0, 0];
  for (let k = n - 2; k >= 0; k--) {
    const q = project(line[k]!);
    const d = Math.hypot(tip[0] - q[0], tip[1] - q[1]);
    if (d > span) {
      span = d;
      from = line[k]!;
      u = [(tip[0] - q[0]) / d, (tip[1] - q[1]) / d];
    }
    if (d >= 1) break;
  }
  if (!from || span < 1e-9) return null;
  return { tip: [tip[0], tip[1]], u, last, from, spanPx: span };
}

/** Boîte d'un obstacle à l'écran (null : tracé vide). */
function obstacleBox(obstacle: TagObstacle, project: (p: LonLat) => readonly [number, number]): ScreenBox | null {
  if ("at" in obstacle) {
    const [x, y] = project(obstacle.at);
    const half = (obstacle.sizePx ?? MARK_BOX_PX) / 2;
    const halfHeight = (obstacle.heightPx ?? obstacle.sizePx ?? MARK_BOX_PX) / 2;
    return { x0: x - half, y0: y - halfHeight, x1: x + half, y1: y + halfHeight };
  }
  const end = lineEnd(obstacle.line, project);
  if (!end) return null;
  const box = tagBox(end.tip, end.u, obstacle.widthPx);
  const [dx, dy] = obstacle.offsetPx ?? [0, 0];
  return { x0: box.x0 + dx, y0: box.y0 + dy, x1: box.x1 + dx, y1: box.y1 + dy };
}

type ScreenPoint = readonly [number, number];

/** Segments à l'écran des traits qui passent dans `area` (course d'une puce), sauf la ligne de la puce `id`. */
function segmentsNear(
  strokes: readonly { id?: string; screen: readonly ScreenPoint[] }[],
  id: string,
  area: ScreenBox,
): [ScreenPoint, ScreenPoint][] {
  const out: [ScreenPoint, ScreenPoint][] = [];
  for (const stroke of strokes) {
    if (stroke.id !== undefined && stroke.id === id) continue;
    for (let k = 0; k + 1 < stroke.screen.length; k++) {
      const a = stroke.screen[k]!;
      const b = stroke.screen[k + 1]!;
      if (Math.max(a[0], b[0]) < area.x0 || Math.min(a[0], b[0]) > area.x1) continue;
      if (Math.max(a[1], b[1]) < area.y0 || Math.min(a[1], b[1]) > area.y1) continue;
      out.push([a, b]);
    }
  }
  return out;
}

/** Puces sans recouvrement, à l'écran (`project` : [lon, lat] → pixels) : chaque puce part du bout de sa ligne (dernier
 *  point du tracé dessiné, dépassement compris) ; tant que sa boîte (`tagBox`) recouvre, marge `TAG_MARGIN_PX`
 *  comprise, celle d'un obstacle (marques d'échelon, puces d'un autre calque) ou d'une puce déjà posée, elle glisse
 *  vers l'extérieur dans l'axe du dernier segment de sa ligne, du minimum qui la dégage plus la marge ; elle avance
 *  aussi, par pas de 2 px, tant qu'elle touche un trait (`strokes` : limites du fuseau, autres lignes), sur
 *  `strokeMaxPx` au plus (`TAG_STROKE_MAX_PX` par défaut ; au-delà, elle reste à la dernière place libre de toute boîte
 *  et effleure le trait) ; glissement total borné par `maxShiftPx`. Les puces se posent dans l'ordre reçu (les
 *  premières sont servies d'abord).
 *
 *  Avec `lateral`, chaque puce a aussi des variantes décalées sur le petit axe de sa boîte : de part et d'autre du bout
 *  (qui reste à `TAG_LATERAL_INSET_PX` de son bord) et, si elle sort du cadre de la carte (`viewport`), juste assez
 *  pour y rester. La combinaison retenue (recherche bornée, `TAG_SEARCH_MAX` combinaisons au plus, sinon variante par
 *  variante) est celle qui laisse le moins de puces hors du cadre, puis qui éloigne le moins la puce la plus éloignée
 *  de son bout, puis le moins l'ensemble, puis qui décale le moins de puces : des lignes presque parallèles aux bouts
 *  voisins n'empilent plus leurs puces loin dans l'axe. La ligne garde son dépassement visible et la puce reste au-delà
 *  de son bout. Fonction pure. */
export function stackTags(
  tags: readonly TagRequest[],
  obstacles: readonly TagObstacle[],
  project: (p: LonLat) => readonly [number, number],
  options: {
    strokes?: readonly TagStroke[];
    marginPx?: number;
    maxShiftPx?: number;
    strokeMaxPx?: number;
    lateral?: boolean;
    viewport?: ScreenBox;
  } = {},
): PlacedTag[] {
  const marginPx = options.marginPx ?? TAG_MARGIN_PX;
  const strokeMax = options.strokeMaxPx ?? TAG_STROKE_MAX_PX;
  const maxShift = options.maxShiftPx ?? TAG_MAX_SHIFT_PX;
  const viewport = options.viewport;
  const blockers = obstacles.map((o) => obstacleBox(o, project)).filter((b): b is ScreenBox => b !== null);
  const strokes = (options.strokes ?? [])
    .filter((stroke) => stroke.line.length >= 2)
    .map((stroke) => ({ id: stroke.id, screen: stroke.line.map((p) => project(p)) }));
  const onScreen = (box: ScreenBox) =>
    !viewport || (box.x0 >= viewport.x0 && box.y0 >= viewport.y0 && box.x1 <= viewport.x1 && box.y1 <= viewport.y1);
  const shifted = (b: ScreenBox, [dx, dy]: readonly [number, number]): ScreenBox => ({
    x0: b.x0 + dx,
    y0: b.y0 + dy,
    x1: b.x1 + dx,
    y1: b.y1 + dy,
  });

  type Variant = (raw: ScreenBox) => [number, number];
  const items = tags.flatMap((tag) => {
    const end = lineEnd(tag.line, project);
    if (!end) return [];
    const { tip, u } = end;
    const rawAt = (shift: number) => tagBox([tip[0] + u[0] * shift, tip[1] + u[1] * shift], u, tag.widthPx);
    // Variantes : centrée sur l'axe de la ligne ; avec `lateral`, décalée sur le petit axe de la boîte (puce d'une
    // ligne presque verticale à l'écran : à droite ou à gauche du bout), et ramenée dans le cadre si elle en sort.
    const variants: Variant[] = [() => [0, 0]];
    if (options.lateral) {
      const axis = Math.abs(u[1]) >= Math.abs(u[0]) ? 0 : 1;
      const at = tip[axis]!;
      const inset = TAG_LATERAL_INSET_PX;
      const pair = (d: number): [number, number] => (axis === 0 ? [d, 0] : [0, d]);
      const lo = (b: ScreenBox) => (axis === 0 ? b.x0 : b.y0);
      const hi = (b: ScreenBox) => (axis === 0 ? b.x1 : b.y1);
      const b0 = rawAt(0);
      // Décalages qui gardent le bout dans la boîte, à `inset` de ses bords.
      const max = at - inset - lo(b0);
      const min = at + inset - hi(b0);
      if (max > 1) variants.push(() => pair(max));
      if (min < -1) variants.push(() => pair(min));
      if (viewport && !onScreen(b0)) {
        const vlo = axis === 0 ? viewport.x0 : viewport.y0;
        const vhi = axis === 0 ? viewport.x1 : viewport.y1;
        // Bout hors du cadre (sur ce petit axe) : la puce peut le laisser hors de sa boîte, au bord du cadre.
        const offFrame = at < vlo || at > vhi;
        variants.push((raw) => {
          let d = hi(raw) > vhi ? vhi - hi(raw) : lo(raw) < vlo ? vlo - lo(raw) : 0;
          if (!offFrame) d = Math.min(Math.max(d, at + inset - hi(raw)), at - inset - lo(raw));
          return pair(d);
        });
      }
    }
    // Traits qui passent dans la course de la puce, toutes variantes comprises.
    const course = [rawAt(0), rawAt(maxShift)];
    const reach = Math.max(0, ...course.map((b) => (b.x1 - b.x0) / 2));
    const near = segmentsNear(strokes, tag.id, {
      x0: Math.min(...course.map((b) => b.x0)) - marginPx - reach,
      y0: Math.min(...course.map((b) => b.y0)) - marginPx - reach,
      x1: Math.max(...course.map((b) => b.x1)) + marginPx + reach,
      y1: Math.max(...course.map((b) => b.y1)) + marginPx + reach,
    });
    return [{ tag, end, rawAt, variants, near }];
  });
  type Item = (typeof items)[number];

  /** Glissement d'une puce dans une variante, compte tenu des boîtes déjà posées. */
  const slide = (item: Item, variant: Variant, others: readonly ScreenBox[]) => {
    const { end, rawAt, near } = item;
    const u = end.u;
    let offset: [number, number] = [0, 0];
    const boxAt = (shift: number) => {
      const raw = rawAt(shift);
      offset = variant(raw);
      return shifted(raw, offset);
    };
    let shift = 0;
    let box = boxAt(0);
    // Place libre de toute boîte d'où part le dégagement des traits en cours (null : aucun en cours).
    let strokeFrom: number | null = null;
    for (let step = 0; step < TAG_MAX_STEPS && shift < maxShift; step++) {
      let need = 0;
      for (const o of others) need = Math.max(need, clearance(box, o, u, marginPx));
      if (need > 0) strokeFrom = null;
      else if (near.length > 0 && near.some(([a, b]) => segmentHitsBox(a, b, box, marginPx))) {
        strokeFrom ??= shift;
        if (shift + STROKE_STEP_PX - strokeFrom > strokeMax) {
          // Pas de place sans trait à moins de `strokeMaxPx` : retour à la place libre de toute boîte.
          shift = strokeFrom;
          break;
        }
        need = STROKE_STEP_PX;
      }
      if (need <= 0) break;
      // + 0,01 px : un dégagement calculé au ras de la marge ne reboucle pas sur un reste d'arrondi.
      shift = Math.min(shift + need + 0.01, maxShift);
      box = boxAt(shift);
    }
    box = boxAt(shift);
    return { shift, box, offset, moved: offset[0] !== 0 || offset[1] !== 0 };
  };

  // Coût d'une combinaison : puces hors du cadre, puis glissement de la plus éloignée (par tranches de 2 px), puis
  // glissement total (au pixel) et puces décalées (une puce décalée vaut 4 px de glissement : centrée à peu près
  // égalité).
  type Pick = ReturnType<typeof slide>;
  const cost = (off: number, worst: number, total: number, moved: number) =>
    off * 1e9 + Math.ceil(worst / 2 - 1e-6) * 1e5 + Math.round(total) * 10 + moved * 40;
  const combinations = items.reduce((n, item) => n * item.variants.length, 1);
  let best: { picks: Pick[]; cost: number } | null = null;
  if (combinations <= TAG_SEARCH_MAX) {
    const picks: Pick[] = [];
    const search = (i: number, boxes: ScreenBox[], off: number, worst: number, total: number, moved: number) => {
      // Borne : le coût ne fait que croître en posant les puces suivantes.
      if (best && cost(off, worst, total, 0) >= best.cost) return;
      if (i === items.length) {
        const full = cost(off, worst, total, moved);
        if (!best || full < best.cost) best = { picks: [...picks], cost: full };
        return;
      }
      const item = items[i]!;
      for (const variant of item.variants) {
        const pick = slide(item, variant, [...blockers, ...boxes]);
        picks.push(pick);
        boxes.push(pick.box);
        search(
          i + 1,
          boxes,
          off + (onScreen(pick.box) ? 0 : 1),
          Math.max(worst, pick.shift),
          total + pick.shift,
          moved + (pick.moved ? 1 : 0),
        );
        boxes.pop();
        picks.pop();
      }
    };
    search(0, [], 0, 0, 0, 0);
  }
  // Trop de combinaisons : variante par variante, la meilleure pour chaque puce dans l'ordre reçu.
  const picks: Pick[] =
    (best as { picks: Pick[] } | null)?.picks ??
    items.reduce<Pick[]>((done, item) => {
      const others = [...blockers, ...done.map((p) => p.box)];
      const options = item.variants.map((variant) => slide(item, variant, others));
      const score = (p: Pick) => cost(onScreen(p.box) ? 0 : 1, p.shift, p.shift, p.moved ? 1 : 0);
      done.push(options.reduce((a, b) => (score(b) < score(a) ? b : a)));
      return done;
    }, []);

  return items.map((item, i) => {
    const { shift, box, offset } = picks[i]!;
    const { end, tag } = item;
    const k = shift / end.spanPx;
    const at: LonLat =
      shift > 0
        ? [end.last[0] + (end.last[0] - end.from[0]) * k, end.last[1] + (end.last[1] - end.from[1]) * k]
        : [end.last[0], end.last[1]];
    return {
      id: tag.id,
      at,
      from: end.from,
      shiftPx: shift,
      offsetPx: offset,
      bearing: bearingDeg(end.from, end.last),
      widthPx: tag.widthPx,
      box,
    };
  });
}

/** Point du nom d'une zone (§4.1) : à mi-profondeur entre ses deux lignes, à l'abscisse transversale `t` (−1 : limite
 *  gauche, +1 : droite), à l'écart de l'axe où se groupent les unités ; null sans les deux lignes ou hors du fuseau
 *  (le point du serveur, sur l'axe, sert alors). */
export function zoneNamePoint(
  frame: Pick<CorridorFrame, "point">,
  lines: readonly { key: MeasureKey; phase: Phase | null; ends: readonly [number, number] }[],
  between: readonly [MeasureKey, MeasureKey],
  t: number,
  depth = 0.5,
): LonLat | null {
  const at = (key: MeasureKey) => {
    const line = lines.find((l) => l.key === key && l.phase === null);
    return line ? line.ends[0] + ((line.ends[1] - line.ends[0]) * (t + 1)) / 2 : null;
  };
  const low = at(between[0]);
  const high = at(between[1]);
  if (low === null || high === null) return null;
  const s = low + (high - low) * depth;
  if (!(s >= 0 && s <= 1)) return null;
  return frame.point(s, t);
}

/** Points essayés pour le nom d'une zone, dans l'ordre de préférence : abscisse transversale `t` d'abord du côté gauche
 *  (opposé aux puces des lignes, au bout droit), à l'écart de l'axe où se groupent les unités, puis profondeur dans la
 *  zone (fraction entre ses deux lignes), du milieu vers les bords. */
export const ZONE_NAME_TS: readonly number[] = [-0.7, -0.5, -0.3, 0.3, 0.5, 0.7];
export const ZONE_NAME_DEPTHS: readonly number[] = [0.5, 0.35, 0.65, 0.22, 0.78];
/** Hauteur de la boîte d'un nom de zone (texte de 13 px, halo de 2 px). */
export const ZONE_NAME_HEIGHT_PX = 20;

/** Largeur estimée d'un nom de zone (police des marques d'échelon en 13 px, interlettrage 0,08 em, halo), par excès. */
export function zoneNameWidthPx(name: string): number {
  return Math.ceil(((tagWidthPx(name) - 14) * 13) / 12 + [...name].length * 1.04 + 4);
}

/** Point du nom d'une zone (§4.1, amendé le 02/10/2026) : le premier des points essayés (`ZONE_NAME_TS` ×
 *  `ZONE_NAME_DEPTHS`, dans le fuseau) dont la boîte à l'écran (`project`) ne recouvre aucun obstacle (symboles des
 *  unités : `obstacles` ; noms déjà posés : `taken`, boîtes à l'écran) et ne touche aucun trait (limites du fuseau, LIMA, lignes de la ZO) ;
 *  faute de point libre, celui qui a le moins de conflits (le premier à égalité). Rend aussi sa boîte (obstacle du nom
 *  suivant) ; null sans les deux lignes de la zone. Fonction pure. */
export function zoneNameSpot(
  frame: Pick<CorridorFrame, "point">,
  lines: readonly { key: MeasureKey; phase: Phase | null; ends: readonly [number, number] }[],
  between: readonly [MeasureKey, MeasureKey],
  name: string,
  project: (p: LonLat) => readonly [number, number],
  o: { obstacles?: readonly TagObstacle[]; strokes?: readonly TagStroke[]; taken?: readonly ScreenBox[] } = {},
): { at: LonLat; box: ScreenBox } | null {
  const width = zoneNameWidthPx(name);
  const boxes = [
    ...(o.obstacles ?? []).map((obstacle) => obstacleBox(obstacle, project)).filter((b): b is ScreenBox => b !== null),
    ...(o.taken ?? []),
  ];
  const screen = (o.strokes ?? []).filter((stroke) => stroke.line.length >= 2).map((stroke) => stroke.line.map((p) => project(p)));
  let best: { at: LonLat; box: ScreenBox; conflicts: number } | null = null;
  for (const t of ZONE_NAME_TS) {
    for (const depth of ZONE_NAME_DEPTHS) {
      const at = zoneNamePoint(frame, lines, between, t, depth);
      if (!at) continue;
      const [x, y] = project(at);
      const box = { x0: x - width / 2, y0: y - ZONE_NAME_HEIGHT_PX / 2, x1: x + width / 2, y1: y + ZONE_NAME_HEIGHT_PX / 2 };
      let conflicts = boxes.filter((b) => boxesOverlap(box, b, 2)).length;
      for (const points of screen)
        for (let k = 0; k + 1 < points.length; k++) {
          if (segmentHitsBox(points[k]!, points[k + 1]!, box, 2)) {
            conflicts++;
            break;
          }
        }
      if (conflicts === 0) return { at, box };
      if (!best || conflicts < best.conflicts) best = { at, box, conflicts };
    }
  }
  return best ? { at: best.at, box: best.box } : null;
}
